<script setup>
import { i18n } from 'boot/i18n';
import { HrListItem, HrOverflowMenuBtn } from 'src/design-system';
import { computed } from 'vue';

import { bodyName } from '../../domain/constants/credentialingBodies';
import { credentialCaption } from '../presenters/qualificationRows';

/**
 * The rows for a group of certifications and licenses.
 *
 * ONE COMPONENT FOR BOTH GROUPS, WITH A VISUAL PROP, and that is a deliberate departure from
 * `ClientCoveragePanel`, which uses a separate component for its ended rows. There the two genuinely
 * differ: a live coverage carries a rank chip and a drag handle and an ended one can carry neither,
 * so a flag would have changed what the row IS. Here the two carry the same label, the same caption
 * and the same actions, and `muted` says only how a past row looks. A second component today would
 * be two copies of one file.
 *
 * It splits the moment a current-only ACTION appears. Verifying a credential is the obvious one:
 * nobody verifies a lapsed certificate, and at that point the row shapes differ rather than the
 * styling, which is the line the sibling panel draws and the right one.
 *
 * NO ROW SAYS "EXPIRED". The section header above the past group already says it, and repeating it
 * on every row is noise — the reasoning `EndedCoverageList` records for its own rows.
 */
defineOptions({ name: 'CredentialList' });

const props = defineProps({
  /** The credentials in this group, already split by the presenter. */
  credentials: {
    type: Array,
    default: () => [],
  },
  /** Renders the group as past. Presentation only: it changes nothing about what a row does. */
  muted: {
    type: Boolean,
    default: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Open this credential for editing. */
  edit: null,
  /** Start a renewal: a new record carrying the same identity and its own window. */
  renew: null,
  /** Remove it from the record. */
  remove: null,
});

const rows = computed(() => {
  return props.credentials.map((credential, index) => ({
    // Indexed because a credential has no id of its own: it is an entry in a list on the person's
    // document, which is what keeps one rule per certificate a property of the data.
    key: `${credential.organization ?? ''}-${credential.jurisdiction ?? ''}-${credential.certificateId ?? ''}-${index}`,
    credential,
    // The level where the body issues more than one thing, otherwise what the body is called: a
    // state license has no level, and a row reading only a certificate number says nothing. The
    // state comes with it, because two licenses are otherwise the same row twice.
    label: [credential.level ?? bodyName(credential.organization), credential.jurisdiction]
      .filter(Boolean)
      .join(' · '),
    caption: credentialCaption(credential),
  }));
});

// Renew sits on both groups on purpose. Renewing a certificate that lapsed last month is the
// commonest reason anybody opens this list, so hiding it on the past group would hide it exactly
// where it is wanted.
const menuOptions = () => [
  { label: i18n.t('common.buttonEdit'), icon: 'edit', value: 'edit' },
  { label: i18n.t('staff.qualifications.actionRenew'), icon: 'autorenew', value: 'renew' },
  {
    group: 'destructive',
    options: [{
      label: i18n.t('common.buttonDelete'), icon: 'delete', value: 'remove', destructive: true,
    }],
  },
];

function onSelect(row, option) {
  if (option.value === 'edit') {
    emit('edit', row.credential);
  } else if (option.value === 'renew') {
    emit('renew', row.credential);
  } else if (option.value === 'remove') {
    emit('remove', row.credential);
  }
} // onSelect()
</script>

<template>
  <hr-list-item
    v-for="row in rows"
    :key="row.key"
    segmented
    :muted="props.muted"
    clickable
    :disable="props.disable"
    :label="row.label"
    :caption="row.caption"
    data-e2e="list-item-staff-credential"
    @click="emit('edit', row.credential)"
  >
    <template #trailing>
      <hr-overflow-menu-btn
        :options="menuOptions()"
        :disable="props.disable"
        @click.stop
        @select="onSelect(row, $event)"
      />
    </template>
  </hr-list-item>
</template>
